import React, { useState, Component } from 'react';
import useWarRiskData from './useWarRiskData.js';
import SummaryBar from './SummaryBar.jsx';
import IncidentTicker from './IncidentTicker.jsx';
import WarRiskMap from './WarRiskMap.jsx';
import ZoneSummaryCards from './ZoneSummaryCards.jsx';
import IncidentsAccordion from './IncidentsAccordion.jsx';

// ─── Error boundary ───────────────────────────────────────────────────────────

class WarRiskErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: 32, background: '#0a1628', minHeight: '100vh' }}>
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '20px 24px', maxWidth: 600,
        }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 600, color: '#ef4444', marginBottom: 8 }}>
            Component render error
          </div>
          <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#c4ccd8', whiteSpace: 'pre-wrap' }}>
            {this.state.error?.message}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 16, padding: '8px 18px', background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skel({ h = 20, w = '100%', r = 6 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: 'linear-gradient(90deg,#1e2d44 0%,#253550 50%,#1e2d44 100%)',
      backgroundSize: '200% 100%',
      animation: 'wr-shimmer 1.5s infinite',
    }} />
  );
}

// ─── Status banner ────────────────────────────────────────────────────────────

function StatusBanner({ message, type = 'warn' }) {
  const colors = {
    warn:  { bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)',  text: '#f59e0b' },
    error: { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   text: '#ef4444' },
    info:  { bg: 'rgba(233,160,32,0.08)',  border: 'rgba(233,160,32,0.18)',   text: '#e9a020' },
  };
  const c = colors[type] || colors.warn;
  return (
    <div style={{
      padding: '10px 16px', borderRadius: 8,
      background: c.bg, border: `1px solid ${c.border}`,
      fontFamily: "'Figtree', sans-serif", fontSize: 12, color: c.text,
      display: 'flex', alignItems: 'flex-start', gap: 8,
    }}>
      <span style={{ flexShrink: 0 }}>{type === 'error' ? '✖' : '⚠'}</span>
      <span>{message}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function WarRiskInner() {
  const { data, loading } = useWarRiskData();
  const [activeZoneId, setActiveZoneId] = useState(null);

  const zones     = data?.zones     || [];
  const incidents = data?.incidents || [];
  const isLive    = data?.source === 'live';
  const isStale   = data?.stale;
  const fetchErr  = data?._fetchError;

  const updatedAt = data?.updated_at
    ? new Date(data.updated_at).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
      }) + ' UTC'
    : null;

  function handleZoneClick(zone) {
    setActiveZoneId(zone.id);
    // Scroll to accordion
    const el = document.getElementById('wr-accordion');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleViewIncidents(zoneId) {
    setActiveZoneId(zoneId);
    const el = document.getElementById('wr-accordion');
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function handleTickerClick(incident) {
    setActiveZoneId(incident.zone_id);
    const el = document.getElementById('wr-accordion');
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  return (
    <>
      <style>{`
        @keyframes wr-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>

      <div style={{ background: '#0a1628', minHeight: '100vh', color: '#fff' }}>

        {/* ── Header ── */}
        <div style={{
          padding: '16px 24px 14px', borderBottom: '1px solid #1e2d44',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          background: '#111a2e',
        }}>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.4px' }}>
              War Risk Intelligence
            </div>
            <div style={{ fontSize: 12, color: '#8892a5', marginTop: 2 }}>
              JWC listed areas · UKMTO incident feed · real-time maritime threat overview
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {!loading && !isLive && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600 }}>
                ⚠ Demo Data
              </span>
            )}
            {isLive && isStale && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, padding: '3px 10px', borderRadius: 20, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600 }}>
                ⚠ Stale
              </span>
            )}
            <div style={{ fontSize: 10, color: '#3a4558', fontFamily: "'Figtree', sans-serif", textAlign: 'right' }}>
              Zones reviewed weekly · Incidents every 3h
              {updatedAt && <><br />Last update: {updatedAt}</>}
            </div>
          </div>
        </div>

        {/* ── Summary bar ── */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#111a2e', borderBottom: '1px solid #1e2d44' }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skel h={10} w="80px" /><Skel h={24} w="60px" /><Skel h={10} w="100px" />
              </div>
            ))}
          </div>
        ) : (
          <SummaryBar zones={zones} incidents={incidents} />
        )}

        {/* ── Incident ticker ── */}
        {loading && <div style={{ height: 48, background: '#0d1525', borderBottom: '1px solid #1e2d44' }} />}
        {!loading && <IncidentTicker incidents={incidents} onChipClick={handleTickerClick} />}

        {/* ── Status banners ── */}
        {!loading && fetchErr && (
          <div style={{ padding: '12px 24px 0' }}>
            <StatusBanner
              type="warn"
              message={`Worker unreachable (${fetchErr}) — showing demo data. Start worker with: npx wrangler dev --config workers/war-risk/wrangler.toml`}
            />
          </div>
        )}
        {!loading && isStale && (
          <div style={{ padding: '12px 24px 0' }}>
            <StatusBanner type="warn" message="Incident data is stale — last UKMTO scrape failed. Showing cached data." />
          </div>
        )}

        {/* ── Content ── */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Map */}
          {/* {loading
            ? <Skel h={480} r={10} />
            : <WarRiskMap zones={zones} incidents={incidents} onZoneClick={handleZoneClick} />
          } */}

          {/* Zone cards */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              {[0,1,2,3,4,5].map(i => <Skel key={i} h={220} r={10} />)}
            </div>
          ) : (
            <ZoneSummaryCards zones={zones} incidents={incidents} onViewIncidents={handleViewIncidents} />
          )}

          {/* Incidents accordion */}
          <div id="wr-accordion">
            {loading
              ? <Skel h={60} r={10} />
              : (
                <IncidentsAccordion
                  incidents={incidents}
                  zones={zones}
                  defaultZoneId={activeZoneId}
                  onClear={() => setActiveZoneId(null)}
                />
              )
            }
          </div>

          {/* No data empty state */}
          {!loading && incidents.length === 0 && (
            <StatusBanner
              type="info"
              message="No recent incidents from UKMTO feed. This may indicate a scrape issue — trigger a manual refresh: POST /api/war-risk/refresh"
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #1e2d44',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          background: '#111a2e',
        }}>
          <div style={{ fontFamily: "'Figtree', sans-serif", fontSize: 11, color: '#3a4558', lineHeight: 1.6 }}>
            Zone boundaries are approximations of JWC Listed Areas for visual reference.
            <br />Always consult your insurer for definitive coverage and additional premium rates.
          </div>
          <div style={{ fontSize: 11, color: '#3a4558', textAlign: 'right' }}>
            Incidents: UKMTO · Zones: JWC
          </div>
        </div>
      </div>
    </>
  );
}

export default function WarRisk() {
  return (
    <WarRiskErrorBoundary>
      <WarRiskInner />
    </WarRiskErrorBoundary>
  );
}
